import { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  FaLanguage,
  FaMicrophone,
  FaPaperclip,
  FaPaperPlane,
  FaRegFileAlt,
  FaRobot,
  FaStop,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import ticketsService from "services/admin/ticketsService";
import { useGetResponseTemplates } from "hooks/useGetResponseTemplates";
import {
  RESPONSE_TEMPLATE_LANGUAGE_LABELS,
  ResponseTemplate,
  ResponseTemplateLanguage,
} from "interfaces/entities/responseTemplate.model";
import { Ticket } from "interfaces/entities/ticket.model";

interface TicketReplyFormProps {
  ticketID: number | string;
  // Полный объект тикета — нужен для подстановки переменных шаблона
  // ({{customerName}}, {{ticketNumber}}) и для выбора языка шаблонов по умолчанию.
  ticket?: Ticket | null;
  // Если true — отправляем внутреннюю заметку (TicketMessage с isInternal=true),
  // клиенту такое сообщение не уходит. Используется во вкладке «Заметки» v2.
  isInternal?: boolean;
  onSent?: () => void;
}

// Подставляет переменные {{token}} в текст шаблона. Неизвестные токены оставляем как есть —
// пусть оператор увидит и сам поправит, а не отправит клиенту пустоту.
const renderTemplate = (text: string, vars: Record<string, string>) =>
  text.replace(/\{\{(\w+)\}\}/g, (full, key) => {
    const value = vars[key];
    return value && value.trim() ? value : full;
  });

// Имя клиента собирается из тикета. Если ничего не известно — оставим обращение нейтральным.
const buildCustomerName = (ticket?: Ticket | null): string => {
  const customer: any = ticket?.customer;
  if (!customer) return "";
  const parts = [customer.firstName, customer.lastName].filter(Boolean);
  if (parts.length) return parts.join(" ");
  return customer.fullName || customer.name || "";
};

// Нормализуем язык тикета в один из языков шаблонов. Если в тикете язык не выставлен — RU
// (как пресет по умолчанию в UI; оператор всё равно увидит все языки в дропдауне).
const resolveLanguage = (ticket?: Ticket | null): ResponseTemplateLanguage | undefined => {
  const raw = (ticket as any)?.language;
  if (typeof raw !== "string") return undefined;
  const upper = raw.toUpperCase();
  if (upper === "RU" || upper === "KK" || upper === "EN") {
    return upper as ResponseTemplateLanguage;
  }
  return undefined;
};

// Подбираем поддерживаемый MIME-тип для записи голоса.
// Браузеры различаются: Chrome/Firefox любят webm/opus, Safari отдаёт mp4.
const pickRecorderMime = (): string | undefined => {
  if (typeof MediaRecorder === "undefined") return undefined;
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
  return candidates.find((m) => MediaRecorder.isTypeSupported(m));
};

const formatDuration = (ms: number) => {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;
};

export const TicketReplyForm = ({ ticketID, ticket, isInternal, onSent }: TicketReplyFormProps) => {
  const [content, setContent] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isRewriting, setIsRewriting] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingMs, setRecordingMs] = useState(0);
  const [translateMenuAnchor, setTranslateMenuAnchor] = useState<HTMLElement | null>(null);
  const [templateMenuAnchor, setTemplateMenuAnchor] = useState<HTMLElement | null>(null);

  // Шаблоны фильтруем по языку тикета — оператору не нужно листать казахские шаблоны,
  // если переписка идёт на русском. Если язык тикета неизвестен — показываем все.
  const ticketLanguage = useMemo(() => resolveLanguage(ticket), [ticket]);
  const { data: templates, isLoading: isTemplatesLoading } = useGetResponseTemplates(
    ticketLanguage ? { language: ticketLanguage } : {},
  );

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordStartRef = useRef<number>(0);
  const tickTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const trimmed = content.trim();

  // Освобождаем медиаресурсы при размонтировании, иначе индикатор записи
  // в браузере останется висеть.
  useEffect(() => {
    return () => {
      if (tickTimerRef.current) clearInterval(tickTimerRef.current);
      recorderRef.current?.state === "recording" && recorderRef.current.stop();
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const appendTranscript = (text: string) => {
    const piece = text.trim();
    if (!piece) {
      toast.info("Расшифровка пустая — попробуйте записать ещё раз чётче");
      return;
    }
    setContent((prev) => (prev.trim() ? `${prev.trim()} ${piece}` : piece));
  };

  const handleTranscribeBlob = async (blob: Blob, fileName?: string) => {
    try {
      setIsTranscribing(true);
      const response = await ticketsService.transcribeAudioMessage(ticketID, blob, { fileName });
      appendTranscript(response?.data?.text || "");
    } catch (error) {
      console.error(error);
    } finally {
      setIsTranscribing(false);
    }
  };

  const startRecording = async () => {
    if (typeof MediaRecorder === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      toast.error("Браузер не поддерживает запись звука");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = pickRecorderMime();
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        if (tickTimerRef.current) {
          clearInterval(tickTimerRef.current);
          tickTimerRef.current = null;
        }
        setIsRecording(false);
        setRecordingMs(0);

        const blobType = recorder.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: blobType });
        chunksRef.current = [];
        if (blob.size === 0) {
          toast.error("Запись пустая");
          return;
        }
        const ext = blobType.includes("mp4") ? "m4a" : blobType.includes("ogg") ? "ogg" : "webm";
        await handleTranscribeBlob(blob, `voice.${ext}`);
      };

      streamRef.current = stream;
      recorderRef.current = recorder;
      recordStartRef.current = Date.now();
      setRecordingMs(0);
      tickTimerRef.current = setInterval(() => {
        setRecordingMs(Date.now() - recordStartRef.current);
      }, 250);
      recorder.start();
      setIsRecording(true);
    } catch (err: any) {
      console.error(err);
      const msg =
        err?.name === "NotAllowedError"
          ? "Доступ к микрофону запрещён. Разрешите его в настройках браузера"
          : "Не удалось начать запись";
      toast.error(msg);
    }
  };

  const stopRecording = () => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state === "recording") {
      recorder.stop();
    }
  };

  const handleFilePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // позволяем выбрать тот же файл повторно
    if (!file) return;
    if (!file.type.startsWith("audio/") && file.type !== "video/mp4") {
      toast.error("Поддерживаются только аудиофайлы");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Файл больше 25 МБ — слишком большой для расшифровки");
      return;
    }
    await handleTranscribeBlob(file, file.name);
  };

  const handleRewrite = async () => {
    if (!trimmed) {
      toast.error("Введите черновик ответа перед улучшением");
      return;
    }
    try {
      setIsRewriting(true);
      const response = await ticketsService.rewriteMessage(ticketID, { content: trimmed });
      const rephrased = response?.data?.rephrased;
      if (typeof rephrased === "string" && rephrased.length > 0) {
        setContent(rephrased);
      } else {
        toast.error("ИИ не вернул улучшенный текст");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsRewriting(false);
    }
  };

  const handleTranslate = async (targetLanguage?: "ru" | "kk" | "en") => {
    setTranslateMenuAnchor(null);
    if (!trimmed) {
      toast.error("Введите текст перед переводом");
      return;
    }
    try {
      setIsTranslating(true);
      const response = await ticketsService.translateMessage(ticketID, {
        content: trimmed,
        ...(targetLanguage ? { targetLanguage } : {}),
      });
      const translated = response?.data?.translated;
      if (typeof translated === "string" && translated.length > 0) {
        setContent(translated);
        const langLabels: Record<"ru" | "kk" | "en", string> = {
          ru: "русский",
          kk: "казахский",
          en: "английский",
        };
        const lang = langLabels[response.data.targetLanguage] || response.data.targetLanguage;
        toast.success(`Переведено на ${lang}`);
      } else {
        toast.error("ИИ не вернул перевод");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleApplyTemplate = (template: ResponseTemplate) => {
    setTemplateMenuAnchor(null);
    const rendered = renderTemplate(template.content, {
      customerName: buildCustomerName(ticket),
      ticketNumber: ticket?.id != null ? String(ticket.id) : "",
    });
    // Если черновик уже есть — спросим, чтобы не затереть напечатанное случайно.
    if (trimmed && !window.confirm("Заменить текущий черновик содержимым шаблона?")) {
      return;
    }
    setContent(rendered);
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!trimmed) {
      toast.error("Сообщение не может быть пустым");
      return;
    }
    try {
      setIsSending(true);
      await ticketsService.sendMessage(ticketID, { content: trimmed, isInternal });
      setContent("");
      toast.success(isInternal ? "Заметка добавлена" : "Ответ отправлен клиенту");
      onSent?.();
    } catch (error) {
      console.error(error);
    } finally {
      setIsSending(false);
    }
  };

  const isBusy = isSending || isRewriting || isTranslating || isTranscribing || isRecording;

  return (
    <div className="ticket-reply-form flex flex-col gap-2">
      <TextField
        label={isInternal ? "Внутренняя заметка" : "Ответ клиенту"}
        placeholder={
          isInternal
            ? "Заметка для команды — клиент её не увидит"
            : "Напишите черновик ответа, продиктуйте голосом или прикрепите аудиофайл"
        }
        value={content}
        onChange={(e) => setContent(e.target.value)}
        multiline
        minRows={2}
        maxRows={12}
        fullWidth
        disabled={isBusy}
      />
      <div className="flex items-center gap-2 justify-end flex-wrap">
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*,video/mp4"
          style={{ display: "none" }}
          onChange={handleFilePick}
        />

        {isTranscribing && (
          <span className="flex items-center gap-1 text-sm text-gray-500">
            <CircularProgress size={12} />
            Распознаём речь...
          </span>
        )}

        {isRecording && (
          <span className="flex items-center gap-1 text-sm" style={{ color: "#d32f2f" }}>
            <span
              style={{
                display: "inline-block",
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: "#d32f2f",
                animation: "pulse 1s infinite",
              }}
            />
            Запись {formatDuration(recordingMs)}
          </span>
        )}

        <Tooltip title="Прикрепить аудиофайл (≤25 МБ)">
          <span>
            <IconButton
              size="small"
              onClick={() => fileInputRef.current?.click()}
              disabled={isBusy}
            >
              <FaPaperclip size={14} />
            </IconButton>
          </span>
        </Tooltip>

        {isRecording ? (
          <Button
            type="button"
            size="small"
            variant="contained"
            color="error"
            onClick={stopRecording}
            startIcon={<FaStop size={12} />}
          >
            Остановить
          </Button>
        ) : (
          <Tooltip title="Записать голосовое сообщение (RU / KK)">
            <span>
              <Button
                type="button"
                size="small"
                variant="outlined"
                onClick={startRecording}
                disabled={isBusy}
                startIcon={<FaMicrophone size={12} />}
              >
                Голосом
              </Button>
            </span>
          </Tooltip>
        )}

        <Tooltip
          title={
            ticketLanguage
              ? `Готовые шаблоны (язык: ${RESPONSE_TEMPLATE_LANGUAGE_LABELS[ticketLanguage]})`
              : "Готовые шаблоны (все языки)"
          }
        >
          <span>
            <Button
              type="button"
              size="small"
              variant="outlined"
              color="secondary"
              onClick={(e) => setTemplateMenuAnchor(e.currentTarget)}
              disabled={isBusy}
              startIcon={
                isTemplatesLoading ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <FaRegFileAlt size={12} />
                )
              }
            >
              Шаблон
            </Button>
          </span>
        </Tooltip>
        <Menu
          anchorEl={templateMenuAnchor}
          open={Boolean(templateMenuAnchor)}
          onClose={() => setTemplateMenuAnchor(null)}
          PaperProps={{ style: { maxWidth: 420, maxHeight: 420 } }}
        >
          {templates && templates.length > 0 ? (
            templates.map((t) => (
              <MenuItem key={t.id} onClick={() => handleApplyTemplate(t)}>
                <ListItemText
                  primary={t.title}
                  secondary={
                    t.content.length > 80 ? `${t.content.slice(0, 80).trimEnd()}…` : t.content
                  }
                  secondaryTypographyProps={{ style: { whiteSpace: "pre-wrap", color: "#888" } }}
                />
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>{isTemplatesLoading ? "Загрузка..." : "Шаблонов пока нет"}</MenuItem>
          )}
          <Divider />
          <MenuItem
            component={Link}
            to="/admin/tickets/templates"
            onClick={() => setTemplateMenuAnchor(null)}
          >
            Управлять шаблонами →
          </MenuItem>
        </Menu>

        <Button
          type="button"
          size="small"
          variant="outlined"
          color="secondary"
          onClick={handleRewrite}
          disabled={isBusy}
          startIcon={
            isRewriting ? <CircularProgress size={12} color="inherit" /> : <FaRobot size={12} />
          }
        >
          {isRewriting ? "Улучшаем..." : "Улучшить через ИИ"}
        </Button>
        <Tooltip title="Перевести черновик (RU / KK / EN)">
          <span>
            <Button
              type="button"
              size="small"
              variant="outlined"
              color="secondary"
              onClick={(e) => setTranslateMenuAnchor(e.currentTarget)}
              disabled={isBusy}
              startIcon={
                isTranslating ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <FaLanguage size={14} />
                )
              }
            >
              {isTranslating ? "Переводим..." : "Перевести"}
            </Button>
          </span>
        </Tooltip>
        <Menu
          anchorEl={translateMenuAnchor}
          open={Boolean(translateMenuAnchor)}
          onClose={() => setTranslateMenuAnchor(null)}
        >
          <MenuItem onClick={() => handleTranslate()}>Авто (RU ↔ KK)</MenuItem>
          <MenuItem onClick={() => handleTranslate("kk")}>На казахский (KK)</MenuItem>
          <MenuItem onClick={() => handleTranslate("ru")}>На русский (RU)</MenuItem>
          <MenuItem onClick={() => handleTranslate("en")}>На английский (EN)</MenuItem>
        </Menu>
        <Button
          onClick={handleSend}
          size="small"
          variant="contained"
          color="primary"
          disabled={isBusy}
          startIcon={
            isSending ? <CircularProgress size={12} color="inherit" /> : <FaPaperPlane size={12} />
          }
        >
          {isSending ? "Отправка..." : isInternal ? "Добавить заметку" : "Отправить"}
        </Button>
      </div>
      <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.3; }
                }
            `}</style>
    </div>
  );
};
